/**
 * Finance · Invoice 360 Workspace (Wave 3).
 *
 * Question this workspace answers:
 *   "Can I understand the entire lifecycle of this invoice?"
 *
 * Contract-driven per Object 021 (Invoice) with drill-downs into
 * Customer, Order, Shipment (Fulfillment), Products/SKU, Payment
 * history, Reminder history, Credit/Debit notes, Attachments, Audit,
 * Timeline, Related documents.
 *
 * Reuses existing primitives only.
 */
import { useMemo, useState } from "react";
import { FileText, Sparkles } from "lucide-react";
import { Card, SectionHeader, TextInput, Button, DeltaChip } from "../../components/lib";
import { TabBar, Drawer, Field, ListRow, Segmented } from "../../components/lib.overlay";
import { useDeepLink, useAutoOpen, RelatedLinksBlock } from "../../lib/integration";
import { DocumentActions } from "../../components/DocumentActions";
import { RecordEditor, FieldSpec } from "../../components/RecordEditor";
import { AuditTrail } from "../../components/AuditTrail";
import { api } from "../../services/api";
import { upsertLocal } from "../../services/store";
import { linksForInvoice } from "../../mocks/entityLinks";
import { INVOICES, Invoice, InvoiceStatus } from "../../mocks/financeData";
import { ORDERS, PRODUCTS } from "../../mocks/commerceData";
import { FULFILLMENTS } from "../../mocks/operationsData";
import { CUSTOMERS_360 } from "../../mocks/customers360Data";

const STATUS_META: Record<InvoiceStatus, { bg: string; fg: string; label: string }> = {
  draft:   { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)", label: "Draft"   },
  sent:    { bg: "var(--ph-ai-tint)",      fg: "var(--ph-ai)",         label: "Sent"    },
  partial: { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)",    label: "Partial" },
  paid:    { bg: "var(--ph-success-tint)", fg: "var(--ph-success)",    label: "Paid"    },
  overdue: { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)",     label: "Overdue" },
};

type InvTab =
  | "overview" | "customer" | "order" | "shipment"
  | "products" | "sku" | "rep" | "payments"
  | "reminders" | "notes" | "comments" | "attachments"
  | "timeline" | "docs" | "audit";

// Coarse AR aging bucket.
function agingBucket(inv: Invoice): "current" | "0-30" | "31-60" | "60+" | "paid" {
  if (inv.status === "paid") return "paid";
  const s = (inv.dueRelative || "").toLowerCase();
  if (s.includes("overdue")) {
    const n = parseInt(s, 10);
    if (isNaN(n)) return "0-30";
    if (n <= 30) return "0-30";
    if (n <= 60) return "31-60";
    return "60+";
  }
  return "current";
}

export default function Invoices() {
  const { q: initialQ, open, clearOpen } = useDeepLink();
  const [tab, setTab] = useState<InvoiceStatus | "all">("all");
  const [q, setQ] = useState(initialQ);
  const [selected, setSelected] = useState<Invoice | null>(null);
  const [wsTab, setWsTab] = useState<InvTab>("overview");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  useAutoOpen(open, INVOICES, (i) => i.id, setSelected);

  const invoiceSchema: FieldSpec[] = [
    { key: "customer",       label: "Customer",         kind: "text",     placeholder: "e.g. Rachid Boutique" },
    { key: "city",           label: "City",             kind: "text",     placeholder: "e.g. Alexandria" },
    { key: "amountEGP",      label: "Amount (EGP)",     kind: "number",   placeholder: "45000" },
    { key: "vatPct",         label: "VAT %",            kind: "number",   placeholder: "14" },
    { key: "description",    label: "Description",      kind: "textarea", placeholder: "50 units · Nocturne Amber 100ml" },
    { key: "paymentMethod",  label: "Payment method",   kind: "text",     placeholder: "Bank transfer" },
    { key: "dueRelative",    label: "Due",              kind: "text",     placeholder: "Net 30" },
    { key: "notes",          label: "Notes",            kind: "textarea", placeholder: "Internal note (optional)" },
  ];

  async function transition(status: string) {
    if (!selected) return;
    try {
      const row = await api.transition("invoices", selected.id, status);
      upsertLocal("invoices", row as { id?: string });
      setSelected(row as unknown as Invoice);
    } catch (e) {
      window.alert((e as Error).message);
    }
  }

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: INVOICES.length };
    INVOICES.forEach((i) => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => INVOICES.filter((i) =>
      (tab === "all" || i.status === tab) &&
      (!q ||
        (i.customer || "").toLowerCase().includes(q.toLowerCase()) ||
        (i.number || "").toLowerCase().includes(q.toLowerCase()) ||
        (i.city || "").toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, q],
  );

  const totalOutstanding = INVOICES
    .filter((i) => i.status !== "paid" && i.status !== "draft")
    .reduce((n, i) => n + (i.amountEGP - i.paidEGP), 0);
  const totalOverdue = INVOICES
    .filter((i) => i.status === "overdue")
    .reduce((n, i) => n + (i.amountEGP - i.paidEGP), 0);
  const totalMonthBilled = INVOICES.reduce((n, i) => n + i.amountEGP, 0);
  const paidPct = Math.round(
    (INVOICES.reduce((n, i) => n + i.paidEGP, 0) / Math.max(1, totalMonthBilled)) * 100,
  );

  const aging = useMemo(() => {
    const buckets = { current: 0, "0-30": 0, "31-60": 0, "60+": 0 };
    INVOICES.forEach((i) => {
      if (i.status === "paid" || i.status === "draft") return;
      const b = agingBucket(i);
      if (b === "paid") return;
      buckets[b] += i.amountEGP - i.paidEGP;
    });
    return buckets;
  }, []);
  const agingMax = Math.max(1, ...Object.values(aging));

  // ── Invoice drill-down data ─────────────────────────────────────
  const invData = useMemo(() => {
    if (!selected) return null;
    const orders = ORDERS.filter((o) => o.customer === selected.customer);
    const c360 = CUSTOMERS_360.find((c) => c.name === selected.customer);
    // Derive SKUs & products from order lines
    const skuMap = new Map<string, { sku: string; name: string; qty: number; unitEGP: number; total: number }>();
    orders.forEach((o) => o.lines.forEach((l) => {
      const cur = skuMap.get(l.sku) || { sku: l.sku, name: l.name, qty: 0, unitEGP: l.unitEGP, total: 0 };
      cur.qty += l.qty;
      cur.total += l.qty * l.unitEGP;
      skuMap.set(l.sku, cur);
    }));
    const skus = Array.from(skuMap.values()).sort((a, b) => b.total - a.total);
    const products = skus.map((s) => ({
      ...s,
      product: PRODUCTS.find((p) => p.sku === s.sku),
    }));
    // Fulfillment / shipment
    const shipment = orders.length > 0
      ? FULFILLMENTS.find((f) => f.orderRef && orders.some((o) => o.reference === f.orderRef))
      : undefined;
    // Payment history — synthesize from paid / partial state
    const payments: { ref: string; amountEGP: number; date: string; method: string }[] = [];
    if (selected.paidEGP > 0) {
      payments.push({
        ref: `PMT-${selected.number.replace(/[^0-9]/g, "").slice(-4) || "0001"}`,
        amountEGP: selected.paidEGP,
        date: selected.status === "paid" ? selected.dueRelative : "Today",
        method: (selected as unknown as { paymentMethod?: string }).paymentMethod || "Bank transfer",
      });
    }
    // Reminder history
    const reminders: { at: string; kind: string; via: string }[] = [];
    if (selected.status === "overdue") {
      reminders.push({ at: "10d ago", kind: "First notice", via: "Email" });
      reminders.push({ at: "3d ago", kind: "Escalation", via: "Email + WhatsApp" });
    } else if (selected.status === "sent") {
      reminders.push({ at: "3d before due", kind: "Auto-reminder", via: "Email · scheduled" });
    }
    // Credit / debit notes — heuristic: none unless invoice has been partially paid
    const notes: { kind: "credit" | "debit"; ref: string; amountEGP: number; reason: string }[] = [];
    if (selected.status === "partial" && selected.amountEGP - selected.paidEGP > 0) {
      notes.push({ kind: "debit", ref: `DN-${selected.number.slice(-4)}`, amountEGP: 0, reason: "Reserved · pending customer confirmation" });
    }
    return { orders, c360, skus, products, shipment, payments, reminders, notes };
  }, [selected]);

  return (
    <div className="space-y-6" data-testid="fn-invoices">
      <Card testId="fn-inv-summary">
        <SectionHeader
          eyebrow="Accounts Receivable · Whole invoice lifecycle"
          title="Invoice health"
          action={<FileText size={14} style={{ color: "var(--ph-ink-muted)" }} />}
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Outstanding</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>
              EGP {(totalOutstanding / 1000).toFixed(0)}K
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Overdue</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-danger)" }}>
              EGP {(totalOverdue / 1000).toFixed(0)}K
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Billed this cycle</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>
              EGP {(totalMonthBilled / 1000).toFixed(0)}K
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Collected</div>
            <div className="text-xl font-semibold tabular-nums mt-1 flex items-center gap-2" style={{ color: "var(--ph-success)" }}>
              {paidPct}% <DeltaChip delta={4.2} trend="up" />
            </div>
          </div>
        </div>
      </Card>

      <Card testId="fn-inv-aging">
        <div className="flex items-baseline justify-between mb-2">
          <SectionHeader eyebrow="Aging" title="Receivables by age" />
          <div data-testid="fn-inv-ap-audit-actions">
            <DocumentActions docKey="ap-audit-weekly" testId="fn-inv-ap-audit" />
          </div>
        </div>
        <div className="space-y-2">
          {(["current", "0-30", "31-60", "60+"] as const).map((b) => {
            const label =
              b === "current" ? "Current" :
              b === "0-30"    ? "0–30 d overdue" :
              b === "31-60"   ? "31–60 d overdue" :
                                "60+ d overdue";
            const fg =
              b === "current" ? "var(--ph-success)" :
              b === "0-30"    ? "var(--ph-warning)" :
              b === "31-60"   ? "var(--ph-danger)"  :
                                "var(--ph-danger)";
            const bar = Math.max(2, (aging[b] / agingMax) * 100);
            return (
              <div key={b} data-testid={`fn-inv-age-${b}`}
                className="grid items-center gap-3 py-1.5"
                style={{ gridTemplateColumns: "1.4fr 3fr 1fr" }}>
                <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{label}</div>
                <div className="h-2 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden" }}>
                  <div style={{ width: `${bar}%`, height: "100%", background: fg }} />
                </div>
                <div className="text-sm tabular-nums text-right" style={{ color: fg }}>
                  EGP {(aging[b] / 1000).toFixed(0)}K
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card testId="fn-inv-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <TextInput
              value={q}
              onChange={setQ}
              placeholder="Search customer, invoice # or city…"
              testId="fn-inv-search"
            />
          </div>
          <Button testId="fn-inv-new" onClick={() => setCreating(true)}>+ New invoice</Button>
        </div>
        <div className="mt-4">
          <TabBar<InvoiceStatus | "all">
            testId="fn-inv-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",     label: "All",     count: counts.all           },
              { key: "sent",    label: "Sent",    count: counts.sent    || 0 },
              { key: "partial", label: "Partial", count: counts.partial || 0 },
              { key: "overdue", label: "Overdue", count: counts.overdue || 0 },
              { key: "paid",    label: "Paid",    count: counts.paid    || 0 },
              { key: "draft",   label: "Draft",   count: counts.draft   || 0 },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="fn-inv-list-card">
        <div className="p-5 flex items-start justify-between flex-wrap gap-3" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Ledger" title={`${filtered.length} invoice${filtered.length === 1 ? "" : "s"}`} />
          <DocumentActions docKey="customer-invoice" testId="fn-inv-doc-actions" />
        </div>
        <div className="p-2" data-testid="fn-inv-list">
          {filtered.map((i) => {
            const s = STATUS_META[i.status];
            const remaining = i.amountEGP - i.paidEGP;
            return (
              <ListRow
                key={i.id}
                testId={`fn-inv-${i.id}`}
                onClick={() => { setSelected(i); setWsTab("overview"); }}
                label={
                  <span className="inline-flex items-center gap-2">
                    <span>{i.customer}</span>
                    <span
                      className="px-1.5 py-0.5 text-[9px] font-semibold uppercase"
                      style={{
                        letterSpacing: "0.14em",
                        background: s.bg,
                        color: s.fg,
                        borderRadius: "var(--ph-radius-sm)",
                      }}
                    >
                      {s.label}
                    </span>
                    <span
                      className="text-[10px] font-mono"
                      style={{ color: "var(--ph-ink-muted)" }}
                    >
                      {i.number}
                    </span>
                  </span>
                }
                sublabel={<>{i.city} · Issued {i.issued} · Due {i.dueRelative}</>}
                trailing={
                  <div className="text-right">
                    <div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>
                      EGP {i.amountEGP.toLocaleString()}
                    </div>
                    {remaining > 0 && i.status !== "draft" && (
                      <div className="text-[10px] tabular-nums mt-0.5" style={{ color: "var(--ph-danger)" }}>
                        EGP {remaining.toLocaleString()} due
                      </div>
                    )}
                  </div>
                }
              />
            );
          })}
        </div>
      </Card>

      <Drawer
        open={Boolean(selected)}
        onClose={() => { setSelected(null); clearOpen(); }}
        title="Invoice 360"
        testId="fn-inv-drawer"
        width={720}
      >
        {selected && invData && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: STATUS_META[selected.status].fg }}>
              {STATUS_META[selected.status].label}
            </div>
            <div
              style={{
                fontFamily: "var(--ph-display-font)",
                fontSize: "1.35rem",
                color: "var(--ph-ink-strong)",
              }}
              className="mt-1 mb-1"
            >
              {selected.customer}
            </div>
            <div className="text-[11px] font-mono mb-3" style={{ color: "var(--ph-ink-muted)" }}>
              {selected.number}
            </div>
            <div className="mb-4 pb-3" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <DocumentActions docKey="customer-invoice" recordId={selected.id} testId={`fn-inv-single-${selected.id}`} compact />
            </div>

            <div className="grid grid-cols-4 gap-3 mb-4">
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Total</div><div className="text-sm font-semibold tabular-nums">EGP {selected.amountEGP.toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Paid</div><div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-success)" }}>EGP {selected.paidEGP.toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Remaining</div><div className="text-sm font-semibold tabular-nums" style={{ color: selected.amountEGP - selected.paidEGP === 0 ? "var(--ph-success)" : "var(--ph-danger)" }}>EGP {(selected.amountEGP - selected.paidEGP).toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Due</div><div className="text-sm font-semibold tabular-nums">{selected.dueRelative}</div></div>
            </div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<InvTab>
                testId="fn-inv-ws-tabs"
                value={wsTab}
                onChange={setWsTab}
                options={[
                  { value: "overview",    label: "Overview"    },
                  { value: "customer",    label: "Customer"    },
                  { value: "order",       label: "Order"       },
                  { value: "shipment",    label: "Shipment"    },
                  { value: "products",    label: "Products"    },
                  { value: "sku",         label: "SKU"         },
                  { value: "rep",         label: "Rep"         },
                  { value: "payments",    label: "Payments"    },
                  { value: "reminders",   label: "Reminders"   },
                  { value: "notes",       label: "CN/DN"       },
                  { value: "comments",    label: "Comments"    },
                  { value: "attachments", label: "Attachments" },
                  { value: "timeline",    label: "Timeline"    },
                  { value: "docs",        label: "Docs"        },
                  { value: "audit",       label: "Audit"       },
                ]}
              />
            </div>

            {wsTab === "overview" && (
              <div data-testid="fn-inv-ws-overview">
                <Field label="Customer"       inline><span className="text-sm">{selected.customer}</span></Field>
                <Field label="City"           inline><span className="text-sm">{selected.city}</span></Field>
                <Field label="Issued"         inline><span className="text-sm">{selected.issued}</span></Field>
                <Field label="Due"            inline><span className="text-sm">{selected.dueRelative}</span></Field>
                <Field label="Payment method" inline><span className="text-sm">{(selected as unknown as { paymentMethod?: string }).paymentMethod || "—"}</span></Field>
                <Field label="Description">
                  <span className="text-sm">{(selected as unknown as { description?: string }).description || "—"}</span>
                </Field>
                <div className="pt-3">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1.5 inline-flex items-center gap-1" style={{ color: "var(--ph-ai)" }}>
                    <Sparkles size={10} /> AI CFO note
                  </div>
                  <div className="text-sm p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                    {selected.status === "overdue" ?
                      "Follow-up recommended. Historical DSO for this customer is 32d — this invoice is trending to 45d. Consider a phone call before a formal reminder." :
                    selected.status === "partial" ?
                      "50% collected. Balance is on track — no action required, but keep monitoring against payment terms." :
                    selected.status === "sent" ?
                      "Sent within terms. Auto-reminder scheduled 3d before due." :
                    selected.status === "draft" ?
                      "Draft not yet issued. Approve to send." :
                      "Cleared. Cash applied to July receipts."}
                  </div>
                </div>
              </div>
            )}

            {wsTab === "customer" && (
              <div data-testid="fn-inv-ws-customer">
                <Field label="Name" inline><span className="text-sm font-semibold">{selected.customer}</span></Field>
                <Field label="City" inline><span className="text-sm">{selected.city}</span></Field>
                {invData.c360 ? (
                  <>
                    <Field label="Segment"        inline><span className="text-sm">{invData.c360.segment}</span></Field>
                    <Field label="Tier"           inline><span className="text-sm">{invData.c360.tier}</span></Field>
                    <Field label="Lifetime orders" inline><span className="text-sm tabular-nums">{invData.c360.lifetimeOrders}</span></Field>
                    <Field label="Lifetime spend" inline><span className="text-sm tabular-nums">EGP {invData.c360.lifetimeSpendEGP.toLocaleString()}</span></Field>
                    <Field label="NPS"            inline><span className="text-sm tabular-nums">{invData.c360.nps}</span></Field>
                    <Field label="Retention risk" inline><span className="text-sm" style={{ color: invData.c360.retentionRisk === "high" ? "var(--ph-danger)" : invData.c360.retentionRisk === "medium" ? "var(--ph-warning)" : "var(--ph-success)" }}>{invData.c360.retentionRisk}</span></Field>
                  </>
                ) : (
                  <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Customer not in Customer 360 master. Consider adding for retention analysis.</div>
                )}
              </div>
            )}

            {wsTab === "order" && (
              <div data-testid="fn-inv-ws-order">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Related orders · {invData.orders.length}</div>
                {invData.orders.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No linked orders for this customer.</div>}
                {invData.orders.map((o) => (
                  <ListRow key={o.id} testId={`fn-inv-ord-${o.id}`}
                    label={<>{o.reference} · {o.status}</>}
                    sublabel={<>{o.channel} · {o.city} · {o.placedRelative}</>}
                    trailing={<span className="text-sm tabular-nums">EGP {o.totalEGP.toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {wsTab === "shipment" && (
              <div data-testid="fn-inv-ws-shipment">
                {invData.shipment ? (
                  <>
                    <Field label="Order ref" inline><span className="text-sm font-mono">{invData.shipment.orderRef}</span></Field>
                    <Field label="Carrier"   inline><span className="text-sm">{invData.shipment.carrier}</span></Field>
                    <Field label="Stage"     inline><span className="text-sm">{invData.shipment.stage}</span></Field>
                    <Field label="Weight"    inline><span className="text-sm tabular-nums">{invData.shipment.weightKg} kg</span></Field>
                    <Field label="ETA"       inline><span className="text-sm">{invData.shipment.eta || "—"}</span></Field>
                  </>
                ) : (
                  <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No fulfilment/shipment record linked. Wholesale customer may pick up directly.</div>
                )}
              </div>
            )}

            {wsTab === "products" && (
              <div data-testid="fn-inv-ws-products">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Products on this account · {invData.products.length}</div>
                {invData.products.map((p) => (
                  <ListRow key={p.sku} testId={`fn-inv-prod-${p.sku}`}
                    label={<>{p.product?.name || p.name} <span className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{p.sku}</span></>}
                    sublabel={<>{p.qty} units · unit EGP {p.unitEGP.toLocaleString()}</>}
                    trailing={<span className="text-sm tabular-nums">EGP {p.total.toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {wsTab === "sku" && (
              <div data-testid="fn-inv-ws-sku">
                {invData.skus.map((s) => (
                  <div key={s.sku} className="grid items-center py-1.5" style={{ gridTemplateColumns: "1.4fr 3fr 1fr 1fr" }}>
                    <div className="text-sm font-mono" style={{ color: "var(--ph-ink-strong)" }}>{s.sku}</div>
                    <div className="text-sm" style={{ color: "var(--ph-ink-default)" }}>{s.name}</div>
                    <div className="text-sm tabular-nums text-right">{s.qty}</div>
                    <div className="text-sm tabular-nums text-right">EGP {s.total.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}

            {wsTab === "rep" && (
              <div data-testid="fn-inv-ws-rep">
                <Field label="Sales rep"   inline><span className="text-sm">{(selected as unknown as { salesRep?: string }).salesRep || "House account · Hassan"}</span></Field>
                <Field label="Territory"   inline><span className="text-sm">{selected.city}</span></Field>
                <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Rep-level commissions are computed at cycle close; see Reports · Weekly Digest.</div>
              </div>
            )}

            {wsTab === "payments" && (
              <div data-testid="fn-inv-ws-payments">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Payments received · {invData.payments.length}</div>
                {invData.payments.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No payments received yet.</div>}
                {invData.payments.map((p) => (
                  <ListRow key={p.ref} testId={`fn-inv-pmt-${p.ref}`}
                    label={p.ref}
                    sublabel={<>{p.method} · {p.date}</>}
                    trailing={<span className="text-sm tabular-nums font-semibold" style={{ color: "var(--ph-success)" }}>EGP {p.amountEGP.toLocaleString()}</span>}
                  />
                ))}
                <div className="mt-3">
                  <Field label="Remaining" inline><span className="text-sm tabular-nums font-semibold" style={{ color: selected.amountEGP - selected.paidEGP === 0 ? "var(--ph-success)" : "var(--ph-danger)" }}>EGP {(selected.amountEGP - selected.paidEGP).toLocaleString()}</span></Field>
                </div>
              </div>
            )}

            {wsTab === "reminders" && (
              <div data-testid="fn-inv-ws-reminders">
                {invData.reminders.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No reminders sent.</div>}
                {invData.reminders.map((r, idx) => (
                  <ListRow key={idx} testId={`fn-inv-rem-${idx}`}
                    label={r.kind}
                    sublabel={r.via}
                    trailing={<span className="text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>{r.at}</span>}
                  />
                ))}
              </div>
            )}

            {wsTab === "notes" && (
              <div data-testid="fn-inv-ws-notes">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Credit / Debit notes</div>
                {invData.notes.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No CN/DN issued for this invoice.</div>}
                {invData.notes.map((n) => (
                  <ListRow key={n.ref} testId={`fn-inv-cndn-${n.ref}`}
                    label={<>{n.kind.toUpperCase()} · {n.ref}</>}
                    sublabel={n.reason}
                    trailing={<span className="text-sm tabular-nums">EGP {n.amountEGP.toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {wsTab === "comments" && (
              <div data-testid="fn-inv-ws-comments">
                <Field label="Internal note" inline><span className="text-sm">{(selected as unknown as { notes?: string }).notes || "—"}</span></Field>
                <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Threaded discussion is captured in the record audit log.</div>
              </div>
            )}

            {wsTab === "attachments" && (
              <div data-testid="fn-inv-ws-attachments">
                <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Customer Invoice PDF is available via the Document Actions above.</div>
                <DocumentActions docKey="customer-invoice" recordId={selected.id} testId={`fn-inv-attach-${selected.id}`} compact />
              </div>
            )}

            {wsTab === "timeline" && (
              <div data-testid="fn-inv-ws-timeline">
                <Field label="Issued"       inline><span className="text-sm">{selected.issued}</span></Field>
                <Field label="Sent"         inline><span className="text-sm">{selected.status === "draft" ? "—" : selected.issued}</span></Field>
                <Field label="Payment(s)"   inline><span className="text-sm tabular-nums">{invData.payments.length}</span></Field>
                <Field label="Reminders"    inline><span className="text-sm tabular-nums">{invData.reminders.length}</span></Field>
                <Field label="Current"      inline><span className="text-sm">{STATUS_META[selected.status].label}</span></Field>
                <AuditTrail collection="invoices" recordId={selected.id} testId="fn-inv-timeline-audit" />
              </div>
            )}

            {wsTab === "docs" && (
              <div data-testid="fn-inv-ws-docs">
                <RelatedLinksBlock links={linksForInvoice(selected.id)} testId="fn-inv-related" />
                <DocumentActions docKey="customer-invoice" recordId={selected.id} testId={`fn-inv-docs-${selected.id}`} compact />
              </div>
            )}

            {wsTab === "audit" && (
              <div data-testid="fn-inv-ws-audit">
                <AuditTrail collection="invoices" recordId={selected.id} testId="fn-inv-audit" />
              </div>
            )}

            <div className="pt-4 flex flex-wrap justify-end gap-2">
              <Button variant="secondary" testId="fn-inv-edit" onClick={() => setEditing(true)}>Edit</Button>
              {selected.status === "draft" && (
                <Button variant="secondary" testId="fn-inv-send" onClick={() => transition("sent")}>Send</Button>
              )}
              {(selected.status === "sent" || selected.status === "partial" || selected.status === "overdue") && (
                <Button testId="fn-inv-mark-paid" onClick={() => transition("paid")}>Mark paid</Button>
              )}
              {selected.status === "sent" && (
                <Button variant="secondary" testId="fn-inv-mark-partial" onClick={() => transition("partial")}>Mark partial</Button>
              )}
            </div>
          </div>
        )}
      </Drawer>

      <RecordEditor<Invoice>
        open={creating}
        onClose={() => setCreating(false)}
        mode="create"
        title="New invoice"
        collection="invoices"
        schema={invoiceSchema}
        initial={{ vatPct: 14, dueRelative: "Net 30" } as Partial<Invoice>}
        sendForApprovalStatus="sent"
        testId="fn-inv-new-drawer"
      />

      {selected && (
        <RecordEditor<Invoice>
          open={editing}
          onClose={() => setEditing(false)}
          mode="edit"
          title={`Edit ${selected.number}`}
          collection="invoices"
          schema={invoiceSchema}
          initial={selected as unknown as Partial<Invoice>}
          onSaved={(row) => setSelected(row)}
          testId="fn-inv-edit-drawer"
        />
      )}
    </div>
  );
}
